import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewEncapsulation
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import {
  DestinationType,
  ErrorMessageComponent,
  FROM,
  TO
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import { DateRange, FromAndTo } from '@com-delta-omnichannel/mach-core-js';
import { takeUntil } from 'rxjs/operators';
import { ViewSearchOptionsComponent } from '../shared/components/view-search-options/view-search-options.component';
import { VacationWidgetBaseComponent } from './vacation-widget-base/vacation-widget-base.component';
@Component({
  selector: 'idp-vacation-widget',
  standalone: true,
  imports: [
    CommonModule,
    ViewSearchOptionsComponent,
    ReactiveFormsModule,
    ErrorMessageComponent
  ],
  templateUrl: './vacation-widget.component.html',
  styleUrls: ['./vacation-widget.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class VacationWidgetComponent
  extends VacationWidgetBaseComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  get hasErrorMessage(): boolean {
    return !!(
      this.errorStateService.airportErrorMessage ||
      this.errorStateService.datesErrorMessage ||
      this.errorStateService.tooFewPeopleErrorMessage ||
      this.errorStateService.maxDatesErrorMessage
    );
  }

  ngAfterViewInit(): void {
    if (this.initializationService.getRequiredElements(this.content)) {
      this.initializeComponents();
      this.setupEventSubscriptions();
      this.initializationService.registerFromAndToInstance(
        this.airportPicker,
        () => this.handleAirportSelection(FROM),
        () => this.handleAirportSelection(TO),
        () => this.handleSwap()
      );
    }
  }

  ngOnDestroy(): void {
    this.destroySubscriptions();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.uiStateService.handleComponentChanges(
      changes,
      this.airportPicker,
      this.destinationType,
      this.lifecycleService.eventService
    );
    document
      .getElementById(
        `from-and-to-${
          this.destinationType === FROM ? 'origin' : 'destination'
        }-button`
      )
      ?.focus();
    if (changes['resetErrors']?.currentValue) {
      this.clearAllErrors();
    }
  }

  private clearAllErrors(): void {
    this.errorStateService.resetErrors();
    this.isSubmitted = false;
    if (this.airportPicker) {
      (this.airportPicker as FromAndTo).setOriginError(false);
      (this.airportPicker as FromAndTo).setDestinationError(false);
    }
  }

  displayOptions(): void {
    this.uiStateService.toggleExpandView();
    this.expandView = this.uiStateService.getCurrentExpandView();
    this.expandEmit.emit(this.expandView);
  }

  formSubmit(event: Event): void {
    if (!this.isIntentionalSubmit) {
      event.preventDefault();
      return;
    }
    this.processFormSubmit();
  }

  private handleSwap(): void {
    const origin = this.vacationWidgetForm.get('origin')?.value;
    const destination = this.vacationWidgetForm.get('destination')?.value;
    this.vacationWidgetForm.patchValue({
      originAirportCode: destination,
      destinationAirportCode: origin
    });
    this.handleErrorCountChange();
  }

  private handleAirportSelection(type: DestinationType): void {
    this.destinationType = type;
    this.openModalEmit.emit(
      this.uiStateService.handleModalToggle(this.closeModal)
    );
  }

  setupEventSubscriptions(): void {
    this.eventService.onDateChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe((dates: DateRange) => this.updateFormDates(dates));
    this.eventService.onDateError$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => (this.errorStateService.datesErrorMessage = ''));
    this.eventService.onPassengerError$
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (error: string) =>
          (this.errorStateService.tooFewPeopleErrorMessage = error)
      );
    this.eventService.onErrorCountChange$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleErrorCountChange());
    this.eventService.onAirportUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.handleErrorCountChange());
    this.eventService.onAirportCodeUpdate$
      .pipe(takeUntil(this.destroy$))
      .subscribe((airportCode: string) => this.updateFormAirport(airportCode));
  }
}
